/* 공유게시글 모더레이션 저장소 — 사용자(선생님)가 공개 공유한 코드를 열람·수정·삭제.
   docs/ops/code-registry.md §5 / admin-api.md §1(D-53).

   레이아웃(huenit-lab-code, public-read):
     public/code/{owner_id}/{asset_id}/workspace.huenit  (블록) | main.py (파이썬)
     public/code/{owner_id}/{asset_id}/meta.json         (AssetMeta)
     public/manifest.json = { assets: [AssetMeta,...] }   ← Lambda 자동 머지(정본)

   ★ 핵심 제약: public/manifest.json 은 Lambda 전용(developer 역할은 못 씀).
     → 모더레이션은 public/code/<owner>/<id>/{meta.json,코드} 객체만 PUT/DELETE 하고, Lambda 가
       public/manifest.json 을 자동 재머지한다. CMS 는 manifest 를 절대 직접 쓰지 않는다.
     삭제는 공개 사본만 내림 — 작성자 개인 저장소(users/{sub}/*) 원본은 건드리지 않는다(takedown).

   s3.js(동결 시그니처)만 사용: getJsonAnon/putObject/deleteObject/listPrefix. */
import { getJsonAnon, putObject, deleteObject, listPrefix } from '../../api/s3.js';
import { isFakeMode, CODE_BUCKET_URL } from '../../config/env.js';

export const PUBLIC_MANIFEST_KEY = 'public/manifest.json';
const url = (key) => (CODE_BUCKET_URL || '') + '/' + key;
const codeFile = (kind) => (kind === 'pythonCode' ? 'main.py' : 'workspace.huenit');
const shareDir = (ownerId, id) => `public/code/${ownerId}/${id}`;

/* AssetMeta → 모더레이션 목록 아이템 */
function mapShare(m) {
  const kind = m.kind || 'blocklyCode';
  return {
    id: m.id,
    ownerId: m.owner_id || '',
    ownerEmail: m.owner_email || '',
    title: m.display_name || '',
    description: m.description || '',
    kind,                                   // blocklyCode | pythonCode
    uploadedAt: m.uploaded_at || '',
    thumbnail: (m.extras && m.extras.thumbnail) || '',
    codeUrl: url(`${shareDir(m.owner_id, m.id)}/${codeFile(kind)}`),
    metaKey: `${shareDir(m.owner_id, m.id)}/meta.json`,
    _meta: m,
  };
}

/* 목록 — public/manifest.json(익명 GET). 없음(404/403) → 빈 배열. 최신순. */
export async function listShares() {
  if (isFakeMode) await ensureFakeSeed();
  let json;
  try {
    json = await getJsonAnon(url(PUBLIC_MANIFEST_KEY));
  } catch (e) {
    if (e && (e.status === 404 || e.status === 403)) return [];
    throw new Error('공유 목록을 읽지 못했습니다: ' + (e && e.message ? e.message : e));
  }
  const assets = json && Array.isArray(json.assets) ? json.assets : [];
  return assets
    .filter((a) => a && a.owner_id && a.id)
    .map(mapShare)
    .sort((a, b) => String(b.uploadedAt).localeCompare(String(a.uploadedAt)));
}

/* 메타 수정(부적절 제목/설명 순화) — public/code/{owner}/{id}/meta.json 의 display_name/description
   만 갱신 후 PUT. Lambda 가 public/manifest.json 재머지. 코드 객체는 미변경. */
export async function updateShareMeta(getCredentials, share, { title, description }) {
  let meta;
  try {
    meta = await getJsonAnon(url(share.metaKey));
  } catch (e) {
    throw new Error('원본 메타를 읽지 못했습니다: ' + (e && e.message ? e.message : e));
  }
  const next = { ...meta, display_name: String(title == null ? meta.display_name : title).trim() };
  if (description != null) next.description = String(description);
  await putObject(getCredentials, share.metaKey, JSON.stringify(next), 'application/json');
  if (isFakeMode) await rebuildFakeManifest(getCredentials);   // live 는 Lambda 가 처리
  return mapShare(next);
}

/* 공개 내림(삭제) — public/code/{owner}/{id}/{코드,meta.json} 삭제. Lambda 재머지.
   원본(users/{sub}/*)은 개발자 역할 권한 밖이라 건드리지 않음 = 공개 사본만 takedown. */
export async function deleteShare(getCredentials, share) {
  const dir = shareDir(share.ownerId, share.id);
  await deleteObject(getCredentials, `${dir}/${codeFile(share.kind)}`);
  await deleteObject(getCredentials, `${dir}/meta.json`);      // meta 삭제 → Lambda 재머지 트리거
  if (isFakeMode) await rebuildFakeManifest(getCredentials);
}

/* ---------- fake 모드: Lambda 부재 대체(데모 리뷰용) ---------- */
const SEED_FLAG = 'adm.fake.sharesSeeded';

async function ensureFakeSeed() {
  try { if (localStorage.getItem(SEED_FLAG)) return; } catch (e) { return; }
  const seed = [
    { id: 'shr-1001', owner_id: 'idsub-jiwoo', owner_email: 'jiwoo.kim@example.com', kind: 'blocklyCode',
      display_name: '무지개 LED 애니메이션', description: '버튼을 누르면 LED가 무지개색으로 순환합니다.',
      ownership: 'public', visibility: 'public', size_bytes: 4210, sha256: 'a'.repeat(64),
      uploaded_at: '2026-06-18T05:30:00Z', extras: {} },
    { id: 'shr-1002', owner_id: 'idsub-minseo', owner_email: 'minseo.lee@example.com', kind: 'blocklyCode',
      display_name: '초음파 거리 경보', description: '거리가 가까워지면 부저가 울립니다.',
      ownership: 'public', visibility: 'public', size_bytes: 3980, sha256: 'b'.repeat(64),
      uploaded_at: '2026-06-19T08:10:00Z', extras: {} },
  ];
  for (const m of seed) {
    await putObject(null, `public/code/${m.owner_id}/${m.id}/workspace.huenit`, '[fake]', 'application/zip');
    await putObject(null, `public/code/${m.owner_id}/${m.id}/meta.json`, JSON.stringify(m), 'application/json');
  }
  await putObject(null, PUBLIC_MANIFEST_KEY, JSON.stringify({ assets: seed }), 'application/json');
  try { localStorage.setItem(SEED_FLAG, '1'); } catch (e) { /* ignore */ }
}

/* fake 에서 Lambda 재머지 흉내 — public/code 하위 각 meta.json 재수집 → manifest 재작성. */
async function rebuildFakeManifest(getCredentials) {
  const keys = await listPrefix(getCredentials, 'public/code/');
  const metaKeys = keys.filter((k) => k.endsWith('/meta.json'));
  const assets = [];
  for (const k of metaKeys) {
    try { assets.push(await getJsonAnon(url(k))); } catch (e) { /* skip */ }
  }
  await putObject(getCredentials, PUBLIC_MANIFEST_KEY, JSON.stringify({ assets }), 'application/json');
}
